import React, { useState, useEffect, useRef } from 'react';

/**
 * 영업시간 입력 필드 — menuEditBottomSheet 의 "판매시간" 입력칸(마스킹 HH:MM) 재사용.
 * 토글 없이 시작~종료 입력만 항상 표시.
 *
 * Props:
 *   - openTime:  string ("HH:MM" | '')
 *   - closeTime: string ("HH:MM" | '')
 *   - onChange: (openTime, closeTime) => void   빈 문자열이면 해제(null 저장)
 */
const BusinessHoursField = ({ openTime = '', closeTime = '', onChange }) => {
  const [startTime, setStartTime] = useState(openTime || '');
  const [endTime, setEndTime] = useState(closeTime || '');
  const [isStartFocused, setIsStartFocused] = useState(false);
  const [isEndFocused, setIsEndFocused] = useState(false);
  const [startError, setStartError] = useState(false);
  const [endError, setEndError] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const startRef = useRef(null);
  const endRef = useRef(null);
  // 최신 입력값 ref — onBlur/자동blur 커밋이 stale closure 대신 항상 최신값을 쓰도록.
  const startValRef = useRef(openTime || '');
  const endValRef = useRef(closeTime || '');

  // props 변경 시 동기화 (부모 저장 후 반영)
  useEffect(() => {
    setStartTime(openTime || '');
    setEndTime(closeTime || '');
    startValRef.current = openTime || '';
    endValRef.current = closeTime || '';
  }, [openTime, closeTime]);

  const validateTime = (timeStr) => {
    if (!timeStr || timeStr.length < 5) return true;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60;
  };

  const formatTimeInput = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `${numbers.slice(0, 2)}:${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}:${numbers.slice(2, 4)}`;
  };

  // 각 값이 '' 이거나 완성된 유효 HH:MM 일 때만 저장 (미완성 입력 중엔 대기).
  // 최신값 ref 에서 읽어 stale closure 로 마감시간이 누락되던 문제 방지.
  const commit = () => {
    if (!onChange) return;
    const s = startValRef.current || '';
    const e = endValRef.current || '';
    const ok = (v) => v === '' || (v.length === 5 && validateTime(v));
    if (ok(s) && ok(e)) onChange(s, e);
  };

  const restoreCursor = (input, cursorPos, prevValue, formatted) => {
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPos;
        if (formatted.includes(':') && !prevValue.includes(':')) {
          if (cursorPos > 2) newCursorPos = cursorPos + 1;
        } else if (!formatted.includes(':') && prevValue.includes(':')) {
          if (cursorPos > 2) newCursorPos = cursorPos - 1;
        }
        if (prevValue.length > formatted.length && cursorPos === 3 && prevValue[2] === ':') {
          newCursorPos = 2;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleStartChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const prevValue = startTime;
    const formatted = formatTimeInput(input.value);
    setStartTime(formatted);
    startValRef.current = formatted;
    restoreCursor(input, cursorPos, prevValue, formatted);
    if (formatted.length === 5) {
      const isValid = validateTime(formatted);
      setStartError(!isValid);
      if (!isValid) setAnimKey((p) => p + 1);
      if (isValid && !endTime) endRef.current?.focus();
    } else {
      setStartError(false);
    }
  };

  const handleEndChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const prevValue = endTime;
    const formatted = formatTimeInput(input.value);
    setEndTime(formatted);
    endValRef.current = formatted;
    restoreCursor(input, cursorPos, prevValue, formatted);
    if (formatted.length === 5) {
      const isValid = validateTime(formatted);
      setEndError(!isValid);
      if (!isValid) setAnimKey((p) => p + 1);
      if (isValid && !startTime) startRef.current?.focus();
      else if (isValid && startTime) endRef.current?.blur();
    } else {
      setEndError(false);
    }
  };

  return (
    <div className="bh-inputs">
      <input
        ref={startRef}
        key={`start-${animKey}`}
        type="text"
        inputMode="numeric"
        className={`time-input ${startTime ? 'has-value' : ''} ${isStartFocused ? 'focused' : ''} ${startError ? 'error' : ''}`}
        placeholder="00:00"
        value={startTime}
        onChange={handleStartChange}
        onFocus={() => setIsStartFocused(true)}
        onBlur={() => { setIsStartFocused(false); commit(); }}
        maxLength="5"
      />
      <span className="time-separator">~</span>
      <input
        ref={endRef}
        key={`end-${animKey}`}
        type="text"
        inputMode="numeric"
        className={`time-input ${endTime ? 'has-value' : ''} ${isEndFocused ? 'focused' : ''} ${endError ? 'error' : ''}`}
        placeholder="23:59"
        value={endTime}
        onChange={handleEndChange}
        onFocus={() => setIsEndFocused(true)}
        onBlur={() => { setIsEndFocused(false); commit(); }}
        maxLength="5"
      />

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-5px); }
          40%, 80% { transform: translateX(5px); }
        }
        .bh-inputs {
          display: flex;
          align-items: center;
          gap: 8.5px;
          margin-top: 14px;
        }
        .time-input {
          width: 139px;
          height: 50px;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          padding: 0 16px;
          font-size: 18px;
          font-weight: 500;
          color: #535353;
          background-color: transparent;
          outline: none;
          text-align: center;
          box-sizing: border-box;
        }
        .time-input::placeholder { color: #CBCBCB; }
        .time-input.has-value:not(.focused) { background-color: #FAFAFB; }
        .time-input.focused { border-color: #FDCD27; caret-color: #FDCD27; }
        .time-input.error { border: 1px solid #FF4444; animation: shake 0.4s ease-in-out; }
        .time-input.error:focus { border: 1px solid #FDCD27; animation: none; }
        .time-separator { font-size: 20px; font-weight: 500; color: #CBCBCB; }
      `}</style>
    </div>
  );
};

export default BusinessHoursField;
